import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Button, Input, message, Modal, Trans } from '@/components'
import { PortProtocol } from '@/consts/machine'
import { Port } from '@/models/instance'

import { Instance } from '../../../models/instance'

const PORT_OP_TYPE = {
  ADD: 1,
  DELETE: 2,
}

type PortCheckResult = 'available' | 'in-use'

function createPortCheckKey(netResID: string, port: number, minPort: number, maxPort: number) {
  return JSON.stringify([netResID, port, minPort, maxPort])
}

export function PublicIPPortModal(props: {
  open: boolean
  instance: Instance
  port?: Port
  mode: number
  onUpdate?: () => void
  onClose: () => void
}) {
  const { open, instance, port, mode, onClose } = props
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [servicePort, setServicePort] = useState<number | undefined>(
    mode == 1 ? port?.srcPort : undefined,
  )
  const [protocol] = useState<PortProtocol>(
    mode == 1 ? port?.protocol || PortProtocol.SSH : PortProtocol.SSH,
  )

  const nat = instance.nats?.[0]
  const netResID = nat?.net.netResID || ''
  const minPublicIPPort = nat?.portRange.min || 1
  const maxPublicIPPort = Math.min(nat?.portRange.max || 65535, 65535)

  // Resolve the actual NAT listen port for this port mapping
  const natListenPort = nat?.portMappings?.items?.find(
    (item) => item.targetPort === port?.srcPort,
  )?.listenPort
  const initialPublicIPPort = mode == 1 ? (natListenPort ?? port?.destPort) : undefined

  const [publicIPPort, setPublicIPPort] = useState<number | undefined>(initialPublicIPPort)
  const isDuplicate =
    servicePort != null &&
    instance.ports?.some(
      (p) => p.srcPort === servicePort && (mode !== 1 || p.srcPort !== port?.srcPort),
    )

  const [servicePortError, setServicePortError] = useState<string>('')
  const [publicIPPortError, setPublicIPPortError] = useState<string>('')
  const [checkingPort, setCheckingPort] = useState(false)
  const lastCheckedPortRef = useRef<{ key: string; result: PortCheckResult } | null>(null)
  const latestPortCheckIDRef = useRef(0)

  const validateServicePort = (value: string) => {
    const numValue = parseInt(value)
    if (!value || isNaN(numValue)) {
      setServicePortError(t('instance.ipPortModal.servicePortRequired'))
      return false
    }
    if (numValue < 1 || numValue > 65535) {
      setServicePortError(t('instance.ipPortModal.portRangeError', { min: 1, max: 65535 }))
      return false
    }
    setServicePortError('')
    return true
  }

  const validatePublicIPPort = (value: string) => {
    const numValue = parseInt(value)
    if (!value) {
      setPublicIPPortError('')
      return true
    }
    if (isNaN(numValue)) {
      setPublicIPPortError(t('instance.ipPortModal.portNumberError'))
      return false
    }
    if (numValue < minPublicIPPort || numValue > maxPublicIPPort) {
      setPublicIPPortError(
        t('instance.ipPortModal.portRangeError', {
          min: minPublicIPPort,
          max: maxPublicIPPort,
        }),
      )
      return false
    }
    // Don't set error message here, let the API check handle it
    return true
  }

  useEffect(() => {
    const checkID = ++latestPortCheckIDRef.current
    const isLatestCheck = () => checkID === latestPortCheckIDRef.current
    const applyPortCheckResult = (result: PortCheckResult) => {
      setPublicIPPortError(result === 'in-use' ? t('instance.ipPortModal.portInUse') : '')
    }

    if (
      publicIPPort == null ||
      Number.isNaN(publicIPPort) ||
      (mode == 1 && publicIPPort === initialPublicIPPort)
    ) {
      setCheckingPort(false)
      setPublicIPPortError('')
      return
    }

    if (publicIPPort < minPublicIPPort || publicIPPort > maxPublicIPPort) {
      setCheckingPort(false)
      setPublicIPPortError(
        t('instance.ipPortModal.portRangeError', {
          min: minPublicIPPort,
          max: maxPublicIPPort,
        }),
      )
      return
    }

    const checkKey = createPortCheckKey(netResID, publicIPPort, minPublicIPPort, maxPublicIPPort)
    const lastCheckedPort = lastCheckedPortRef.current

    if (lastCheckedPort?.key === checkKey) {
      applyPortCheckResult(lastCheckedPort.result)
      setCheckingPort(false)
      return
    }

    setCheckingPort(true)
    setPublicIPPortError('')

    const controller = new AbortController()
    const timeoutID = setTimeout(async () => {
      if (!isLatestCheck() || controller.signal.aborted) return

      try {
        const res = await API.checkNATPort(
          {
            res_id: netResID,
            listen_port: publicIPPort,
          },
          { signal: controller.signal },
        )
        if (!isLatestCheck() || controller.signal.aborted) return

        if (res.code === 194 || res.code === 0) {
          const result: PortCheckResult = res.code === 194 ? 'in-use' : 'available'
          lastCheckedPortRef.current = { key: checkKey, result }
          applyPortCheckResult(result)
        } else {
          setPublicIPPortError(t('instance.ipPortModal.checkPortFailed'))
        }
      } catch (error) {
        if (!isLatestCheck() || controller.signal.aborted) return
        console.error('Error checking port:', error)
        setPublicIPPortError(t('instance.ipPortModal.checkPortFailed'))
      } finally {
        if (isLatestCheck() && !controller.signal.aborted) {
          setCheckingPort(false)
        }
      }
    }, 500)

    return () => {
      clearTimeout(timeoutID)
      controller.abort()
      if (isLatestCheck()) {
        latestPortCheckIDRef.current += 1
      }
    }
  }, [publicIPPort, mode, initialPublicIPPort, netResID, minPublicIPPort, maxPublicIPPort, t])

  const hasChanges = () => {
    if (!port) return true
    return publicIPPort !== initialPublicIPPort || protocol !== port.protocol
  }

  const onOK = () => {
    if (!servicePort || isNaN(servicePort)) {
      setServicePortError(t('instance.ipPortModal.servicePortRequired'))
      return
    }
    if (servicePort < 1 || servicePort > 65535) {
      setServicePortError(t('instance.ipPortModal.portRangeError', { min: 1, max: 65535 }))
      return
    }
    if (publicIPPort != null && !isNaN(publicIPPort)) {
      if (publicIPPort < minPublicIPPort || publicIPPort > maxPublicIPPort) {
        setPublicIPPortError(
          t('instance.ipPortModal.portRangeError', {
            min: minPublicIPPort,
            max: maxPublicIPPort,
          }),
        )
        return
      }
    }

    setLoading(true)

    if (mode == 1) {
      // Edit mode: POST ADD with same srcPort, backend handles old mapping removal
      API.updateInstancePort({
        request_id: instance.displayID,
        type: PORT_OP_TYPE.ADD,
        add_port_op: {
          ports: JSON.stringify([{ srcPort: servicePort, destPort: publicIPPort, protocol }]),
        },
        remove_port_op: {
          ports: JSON.stringify([]),
        },
      })
        .then((res) => {
          if (res.code === 0) {
            message.success(t('instance.access.message.taskSubmitted'))
            props.onUpdate?.()
            onClose && onClose()
          }
        })
        .catch((error) => {
          console.error('Error updating port:', error)
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      // Add mode
      API.updateInstancePort({
        request_id: instance.displayID,
        type: PORT_OP_TYPE.ADD,
        add_port_op: {
          ports: JSON.stringify([{ srcPort: servicePort, destPort: publicIPPort, protocol }]),
        },
        remove_port_op: {
          ports: JSON.stringify([]),
        },
      })
        .then((res) => {
          if (res.code === 0) {
            message.success(t('instance.access.message.taskSubmitted'))
            props.onUpdate?.()
            onClose && onClose()
          }
        })
        .catch((error) => {
          console.error('Error updating port:', error)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  return (
    <Modal
      className="w-[640px]"
      open={open}
      onClose={() => {
        onClose && onClose()
      }}
    >
      <Modal.Header>
        {mode == 1 ? (
          <Trans i18nKey="instance.ipPortModal.titleEdit">Edit Port Binding Rules</Trans>
        ) : (
          <Trans i18nKey="instance.ipPortModal.title">New Port Binding Rules</Trans>
        )}
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <div>
            <h3 className="text-primary t-sb mb-2 flex">
              <Trans i18nKey="instance.ipPortModal.instanceServicePort">
                Instance Service Port
              </Trans>
              <div className="text-red-500">*</div>
            </h3>
            <Input
              type="number"
              value={servicePort}
              min={1}
              max={65535}
              disabled={loading || mode == 1}
              onChange={(e) => {
                const value = parseInt(e.target.value)
                setServicePort(value)
              }}
              validator={validateServicePort}
            />
            {isDuplicate && (
              <div className="mt-2 t-sm text-red-500">{t('instance.ipPortModal.portExists')}</div>
            )}
            {servicePortError && <div className="mt-2 t-sm text-red-500">{servicePortError}</div>}
            <div className="mt-2 t-sm text-secondary">
              {t('instance.ipPortModal.servicePortHint')}
            </div>
          </div>
          <div>
            <h3 className="text-primary t-sb mb-2">
              <Trans i18nKey="instance.ipPortModal.publicIPPort">Public IP Port</Trans>
            </h3>
            <Input
              type="number"
              value={publicIPPort}
              min={minPublicIPPort}
              max={maxPublicIPPort}
              disabled={loading}
              onChange={(e) => {
                const value = parseInt(e.target.value)

                if (!Object.is(value, publicIPPort)) {
                  latestPortCheckIDRef.current += 1

                  const shouldCheckPort =
                    Number.isFinite(value) &&
                    value >= minPublicIPPort &&
                    value <= maxPublicIPPort &&
                    !(mode == 1 && value === initialPublicIPPort)

                  if (shouldCheckPort) {
                    const checkKey = createPortCheckKey(
                      netResID,
                      value,
                      minPublicIPPort,
                      maxPublicIPPort,
                    )
                    const lastCheckedPort = lastCheckedPortRef.current

                    if (lastCheckedPort?.key === checkKey) {
                      setPublicIPPortError(
                        lastCheckedPort.result === 'in-use'
                          ? t('instance.ipPortModal.portInUse')
                          : '',
                      )
                      setCheckingPort(false)
                    } else {
                      setCheckingPort(true)
                    }
                  } else {
                    setCheckingPort(false)
                    if (mode == 1 && value === initialPublicIPPort) {
                      setPublicIPPortError('')
                    }
                  }
                }

                setPublicIPPort(value)
              }}
              validator={validatePublicIPPort}
            />
            {checkingPort ? (
              <div className="mt-2 t-sm text-secondary">
                {t('instance.ipPortModal.checkingPort')}
              </div>
            ) : publicIPPortError ? (
              <div className="mt-2 t-sm text-red-500">{publicIPPortError}</div>
            ) : (
              <div className="mt-2 t-sm text-secondary">
                {t('instance.ipPortModal.publicIPPortHint', {
                  min: minPublicIPPort,
                  max: maxPublicIPPort,
                })}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            onClose && onClose()
          }}
        >
          <Trans i18nKey="instance.ipPortModal.cancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={
            !servicePort ||
            loading ||
            !hasChanges() ||
            !!isDuplicate ||
            !!servicePortError ||
            !!publicIPPortError ||
            checkingPort
          }
          onClick={onOK}
        >
          {mode == 1 ? (
            <Trans i18nKey="instance.ipPortModal.update">Update</Trans>
          ) : (
            <Trans i18nKey="instance.ipPortModal.create">Create</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
